import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { loadStripe } from '@stripe/stripe-js';
import { Elements } from '@stripe/react-stripe-js';
import { MessageCircle, Palette, Ticket } from 'lucide-react';
import PaymentForm from './PaymentForm';
import { trackEvent } from '../lib/metaPixel';

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

const PILLARS = [
  { icon: MessageCircle, title: 'Connect', text: 'Message members you click with and build meaningful friendships and connections.' },
  { icon: Palette, title: 'Express Yourself', text: 'Personalize your profile with social links, wallpapers, media and widgets.' },
  { icon: Ticket, title: 'Get More Access', text: 'Live expert sessions (coming soon), early access to events plus a member discount on tickets, and partner perks.' },
];

const PLANS = [
  { id: 'monthly', name: 'Monthly', price: '$7', note: 'per month', priceId: process.env.REACT_APP_STRIPE_MONTHLY_PRICE_ID, payLabel: '$7 per month' },
  { id: 'yearly', name: 'Yearly', price: '$70', note: '$5.83/mo · save 17%', priceId: process.env.REACT_APP_STRIPE_YEARLY_PRICE_ID, best: true, payLabel: '$70 per year' },
  { id: 'lifetime', name: 'Lifetime', price: '$97', note: 'pay once · founding member', priceId: process.env.REACT_APP_STRIPE_LIFETIME_PRICE_ID, payLabel: '$97 one time' },
];

/* The Sirati+ offer. Used at the end of onboarding and on the upgrade page.
   onPaid() fires after a successful payment; onSkip() is the free path. */
const SiratiPlusOffer = ({ userId, onPaid, onSkip, skipLabel = 'Not ready for Sirati+? Continue with the free community.' }) => {
  const [planId, setPlanId] = useState('yearly');
  const [showPay, setShowPay] = useState(false);
  const plan = PLANS.find((p) => p.id === planId);

  return (
    <div data-testid="sirati-plus-offer">
      <h2 className="font-heading text-2xl font-semibold text-text-main tracking-tight mb-2">Connect More with Sirati+</h2>
      <p className="text-base text-text-main leading-relaxed mb-5">
        You're already part of the community. Sirati+ helps you build deeper connections, express yourself, and get more access to the people and experiences that make the journey meaningful.
      </p>

      <div className="space-y-2 mb-6">
        {PILLARS.map(({ icon: Icon, title, text }) => (
          <div key={title} className="flex items-center gap-3 p-3 rounded-arch-lg bg-white/60 dark:bg-white/10 border border-primary/15">
            <span className="w-10 h-10 shrink-0 rounded-2xl flex items-center justify-center text-white" style={{ background: 'linear-gradient(135deg, #4AADA8, #A99CC8)' }}>
              <Icon className="w-5 h-5" aria-hidden="true" />
            </span>
            <span className="min-w-0">
              <span className="block font-heading font-semibold text-[15px] text-text-main">{title}</span>
              <span className="block text-[13px] leading-snug text-text-main">{text}</span>
            </span>
          </div>
        ))}
      </div>

      <div role="radiogroup" aria-label="Choose your Sirati+ plan" className="grid grid-cols-3 gap-2 mb-2 pt-2">
        {PLANS.map((p) => {
          const selected = p.id === planId;
          return (
            <button
              key={p.id}
              type="button"
              role="radio"
              aria-checked={selected}
              onClick={() => { setPlanId(p.id); }}
              className={`relative min-h-[88px] px-1.5 py-3 rounded-arch-lg text-center flex flex-col gap-0.5 border-2 transition-all active:scale-[0.98] ${
                selected ? 'border-primary bg-white dark:bg-white/15 shadow-lg shadow-primary/20 -translate-y-1' : 'border-transparent bg-white/60 dark:bg-white/10 hover:border-primary/30'
              }`}
              data-testid={`plan-${p.id}`}
            >
              {p.best && (
                <span className="absolute -top-2.5 left-1/2 -translate-x-1/2 px-2.5 py-0.5 bg-primary text-white text-[10px] font-bold rounded-full whitespace-nowrap">Best value</span>
              )}
              <span className="text-xs text-text-muted">{p.name}</span>
              <span className="font-heading text-2xl font-semibold text-text-main">{p.price}</span>
              <span className="text-[11px] leading-tight text-text-muted">{p.note}</span>
            </button>
          );
        })}
      </div>
      <p className="text-xs text-text-muted text-center mb-4">Beta pricing for early members</p>

      {showPay ? (
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}>
          <Elements stripe={stripePromise}>
            <PaymentForm
              key={plan.id}
              priceId={plan.priceId}
              tier={plan.id}
              userId={userId}
              onSuccess={onPaid}
              buttonLabel={`Pay ${plan.payLabel}`}
            />
          </Elements>
          <p className="text-[11px] text-text-muted text-center mt-3">
            {plan.id === 'lifetime'
              ? 'One payment, no renewals. Payments are non-refundable except where law requires.'
              : 'Cancel anytime from your profile; access continues until the end of the period.'}
          </p>
        </motion.div>
      ) : (
        <button
          type="button"
          onClick={() => { setShowPay(true); trackEvent('InitiateCheckout', { plan: plan.id }); }}
          className="w-full min-h-[48px] py-3 bg-primary text-white rounded-full text-base font-semibold btn-hover active:scale-[0.98]"
          data-testid="start-sirati-plus"
        >
          Start My Sirati+ Journey
        </button>
      )}

      <button
        type="button"
        onClick={onSkip}
        className="block w-full min-h-[44px] mt-3 text-sm text-text-main underline underline-offset-4 hover:text-primary"
        data-testid="continue-free"
      >
        {skipLabel}
      </button>
    </div>
  );
};

export default SiratiPlusOffer;
